import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeOnboardIndustryRequestLook = 'industry-request-chip' | 'industry-request-chip-open' | 'industry-request-send';
export type WedgeOnboardIndustryRequestFieldVariant = 'wedge-onb-request';

const LOOKS = new Set<string>(['industry-request-chip', 'industry-request-chip-open', 'industry-request-send']);
export const isWedgeOnboardIndustryRequestLook = (look: string): look is WedgeOnboardIndustryRequestLook => LOOKS.has(look);
export const isWedgeOnboardIndustryRequestFieldVariant = (variant: unknown): variant is WedgeOnboardIndustryRequestFieldVariant => variant === 'wedge-onb-request';

/* Released onboarding industry request (prep-screens-1 OnboardIndustry, non-compact): the Don't see your industry card at the end of the catalog
   grid, the Send request button and the request input. The card is a left aligned flex column, 124px tall at least, with 14px of padding, a 16px
   radius, a 1.5px dashed border and a 200ms fade of all properties; closed it is the surface fill on the bold separator, open it is the accent
   tint on the accent. The caller owns the entrance animation, whose delay grows with the catalog size, and the will-change line. The Send button
   is full width with a 10px top margin, 11px by 12px of padding, an 11px radius, no border and 14px bold type: the on-accent ink on the accent
   while a request is typed, the tertiary ink on the elevated fill and the default cursor while it is held off. The input is full width with the
   same padding, the elevated fill, the card border, an 11px radius, 14.5px type and no outline of its own (the shipped onboarding field rules
   paint the focus ring from the caller class). Each reads the exact legacy roles, and the declaration order is the order of the source. The
   caller owns the label, the handlers, the type, the shipped wedge-pill and wedge-onb-field classes and every attribute. No look draws the
   transparent target: the industry- prefix leaves it off, and the card is 124px tall already. */
const CHIP_PADDING = '14px 14px', CHIP_RADIUS = 16, CHIP_GAP = 10, CHIP_MIN = 124, CHIP_FADE = 200;
const SEND_PADDING = '11px 12px', SEND_RADIUS = 11, SEND_FONT = 14, SEND_FADE = 200;
const FIELD_PADDING = '11px 12px', FIELD_RADIUS = 11, FIELD_FONT = 14.5;

export function wedgeOnboardIndustryRequestStyle(look: WedgeOnboardIndustryRequestLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'industry-request-send') return {
    marginTop: 10, width: '100%', padding: SEND_PADDING, background: disabled ? r('bgElev') : r('accent'), color: disabled ? r('labelTer') : r('onAccent'),
    border: 'none', borderRadius: SEND_RADIUS, fontSize: SEND_FONT, fontWeight: 700, fontFamily: r('font'), cursor: disabled ? 'default' : 'pointer',
    transition: `all ${SEND_FADE}ms ${r('ease')}`,
  };
  const open = look === 'industry-request-chip-open';
  return {
    background: open ? r('accentTint') : r('surface'), border: `1.5px dashed ${open ? r('accent') : r('separatorBold')}`, borderRadius: CHIP_RADIUS,
    padding: CHIP_PADDING, fontFamily: r('font'), cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: CHIP_GAP,
    transition: `all ${CHIP_FADE}ms ${r('ease')}`, textAlign: 'left', minHeight: CHIP_MIN, position: 'relative',
  };
}

export function wedgeOnboardIndustryRequestFieldStyle(_variant: WedgeOnboardIndustryRequestFieldVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    width: '100%', padding: FIELD_PADDING, boxSizing: 'border-box', background: r('bgElev'), border: `1px solid ${r('cardBorder')}`,
    borderRadius: FIELD_RADIUS, fontSize: FIELD_FONT, color: r('label'), fontFamily: r('font'), outline: 0,
  };
}
